import type { WorkbookSource } from '@erp/shared-types';

/** `value` as JSON with object keys sorted, so equal values read the same. */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object')
    return `{${Object.entries(value)
      .filter(([, item]) => item !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`)
      .join(',')}}`;
  return JSON.stringify(value);
}

/**
 * A schema group's "all" members leave out a set: in any order, and none
 * listed the same as no list.
 */
function normalizedMembers(source: WorkbookSource): WorkbookSource {
  if (source.kind !== 'schema-group' || source.members.mode !== 'all')
    return source;
  const exclude = [...(source.members.exclude ?? [])].sort();
  return {
    ...source,
    members: exclude.length ? { mode: 'all', exclude } : { mode: 'all' },
  };
}

/** What a Source reads, apart from what it is called and where it is filed. */
function sourceReading(source: WorkbookSource): string {
  const {
    id: _id,
    name: _name,
    folder: _folder,
    ...reading
  } = normalizedMembers(source);
  return canonicalJson(reading);
}

/**
 * Whether `sources` already has a Source reading what `next` reads. Forecast
 * snapshots never repeat one another: each is added to be filtered to its own runs.
 */
export function isDuplicateSource(
  sources: readonly WorkbookSource[],
  next: WorkbookSource,
): boolean {
  if (next.kind === 'forecast-snapshots') return false;
  const reading = sourceReading(next);
  return sources.some((source) => sourceReading(source) === reading);
}
